import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AdminNameCell, AdminRowActions, rowClickHandler } from '../admin/AdminRowCells';
import axiosRestaurantInstance from './AxiosRestaurantInstance';
import { fetchRestaurantOrderHistory, formatTableNumberDisplay } from './orderApi';
import { formatOrderMobileDisplay, formatPaymentMethodDisplay, formatOrderDateTime, formatDisplayOrderNo, resolveOrderMobile } from './formatDisplay';
import { pickDefaultRestaurantFromList } from './restaurantApi';
import SearchableSelect from './SearchableSelect';
import OrderStatusPill from './ui/OrderStatusPill';
import OrderBillingCell from './ui/OrderBillingCell';
import OrdersSortableTh from './OrdersSortableTh';
import OrdersTablePagination from './OrdersTablePagination';
import { getOrderSortValue, sortOrderList } from './ordersTableUtils';
import { buildSortHandlers } from './tableSortUtils';
import './css/OrderHistory.css';
import './css/TodayOrder.css';
import './css/Menu.css';
import './css/Inventory.css';
import './css/OrdersTablePagination.css';

const COMPLETED_ORDER_NORMS = new Set(['COMPLETED', 'COMPLETE', 'CMP', 'DELIVERED', 'DEL', 'PAID']);
const CANCELLED_ORDER_NORMS = new Set(['CANCELLED', 'CANCELED', 'CAN', 'REJECTED', 'REJ', 'UNDELIVERED', 'UND']);

/** Maps an order status onto the order # pill colour (green done, red cancelled, grey otherwise). */
function orderPillStatus(rawStatus) {
    const norm = String(rawStatus ?? '').trim().toUpperCase().replace(/[\s_-]+/g, '');
    if (COMPLETED_ORDER_NORMS.has(norm)) return 'ACTIVE';
    if (CANCELLED_ORDER_NORMS.has(norm)) return 'INACTIVE';
    return rawStatus || '';
}

/** Plain clicks on the name link navigate with router state; modified clicks still open a new tab. */
function nameLinkClickCapture(open) {
    return (event) => {
        if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        if (!event.target.closest('a.eats-admin-link')) return;
        event.preventDefault();
        open();
    };
}

const OrderHistory = ({ userId, orderTypeFilter = 'offline' }) => {
    const navigate = useNavigate();
    const [restaurants, setRestaurants] = useState([]);
    const [selectedRestaurant, setSelectedRestaurant] = useState('');
    const [orders, setOrders] = useState([]);
    const [historyPage, setHistoryPage] = useState(1);
    const [historyPageSize, setHistoryPageSize] = useState(10);
    const [historySort, setHistorySort] = useState(null);

    const user = JSON.parse(localStorage.getItem('user'));
    const currentUserId = userId || (user ? user.userId : null);

    const restaurantSelectOptions = useMemo(
        () =>
            restaurants.map((r) => ({
                value: r.RestaurantId,
                label: r.RestaurantName ?? `Restaurant ${r.RestaurantId}`,
            })),
        [restaurants]
    );

    // Fetch restaurants on component mount
    useEffect(() => {
        const token = localStorage.getItem('Token')
        const fetchRestaurants = async () => {
            if (!currentUserId) {
                console.error('User ID is not available');
                return;
            }

            try {
                const response = await axiosRestaurantInstance.get(`/user/${currentUserId}/restaurants`,{
                    headers: {
                        'Authorization': `Bearer ${token}`, // Set the token in the Authorization header
                    }
                });
                console.log('Fetched restaurants:', response.data);
                if (Array.isArray(response.data)) {
                    setRestaurants(response.data);
                } else {
                    console.error('Expected an array but got:', response.data);
                }
            } catch (error) {
                console.error('Error fetching restaurants:', error);
            }
        };

        fetchRestaurants();
    }, [currentUserId]);

    useEffect(() => {
        if (restaurants.length > 0 && !selectedRestaurant && orderTypeFilter === 'offline') {
            const row = pickDefaultRestaurantFromList(restaurants);
            const id = row?.RestaurantId ?? row?.restaurantId ?? row?.restaurant_id;
            if (id) setSelectedRestaurant(id);
        }
    }, [restaurants, orderTypeFilter, selectedRestaurant]);

    // Fetch orders based on selected restaurant
    useEffect(() => {
        const fetchOrdersByRestaurant = async () => {
            try {
                // Get orders from localStorage (static orders)
                const localOrders = JSON.parse(localStorage.getItem('localOrders') || '[]');
                let filteredLocalOrders = [];
                
                if (orderTypeFilter === 'offline') {
                    // For offline, get all orders with tableNumber
                    filteredLocalOrders = localOrders.filter(order => {
                        const tableNum = order.tableNumber || order.table_number || order.TableNumber;
                        return tableNum !== null && tableNum !== undefined && tableNum !== '' && String(tableNum).trim() !== '';
                    });
                } else {
                    // For online, filter by restaurantId and no tableNumber, and exclude saved/delivered orders
                    // Saved orders (NEW) and delivered orders (DELIVERED) should only appear in offline table
                    if (selectedRestaurant) {
                        filteredLocalOrders = localOrders.filter(order => {
                            const tableNum = order.tableNumber || order.table_number || order.TableNumber;
                            const hasTableNumber = tableNum !== null && tableNum !== undefined && tableNum !== '' && String(tableNum).trim() !== '';
                            const orderStatus = (order.orderStatus || order.OrderStatus || order.order_status || 'NEW').toString().toUpperCase();
                            const isSavedOrder = orderStatus === 'NEW';
                            const isDeliveredOrder = orderStatus === 'DELIVERED';
                            
                            // Online orders: no table number AND not saved/delivered orders
                            return (order.restaurantId === selectedRestaurant || order.restaurantId === String(selectedRestaurant)) 
                                && !hasTableNumber 
                                && !isSavedOrder 
                                && !isDeliveredOrder;
                        });
                    }
                }

                // Offline / dine-in: merge localStorage with API history so payment method shows
                if (orderTypeFilter === 'offline') {
                    let apiOrders = [];
                    try {
                        if (selectedRestaurant) {
                            const historyOrders = await fetchRestaurantOrderHistory(
                                selectedRestaurant,
                                { orderSource: 'MB' }
                            );
                            apiOrders = (historyOrders || []).map((order) => ({
                                ...order,
                                paymentMethod:
                                    order.paymentMethod ??
                                    order.payment_method ??
                                    order.payment?.paymentMethod ??
                                    order.payment?.payment_method ??
                                    null,
                                tableNumber:
                                    order.tableNumber ??
                                    order.table_number ??
                                    order.tableNo ??
                                    order.table_no ??
                                    order.TableNumber ??
                                    null,
                            }));
                        }
                    } catch (apiError) {
                        console.log('Order history API fetch failed, using localStorage only', apiError);
                    }

                    const byId = new Map();
                    apiOrders.forEach((o) => {
                        const id = o.orderId ?? o.OrderId;
                        if (id == null) return;
                        byId.set(String(id), o);
                    });
                    filteredLocalOrders.forEach((o) => {
                        const id = o.orderId ?? o.OrderId;
                        if (id == null) return;
                        const key = String(id);
                        const existing = byId.get(key);
                        if (existing) {
                            byId.set(key, {
                                ...o,
                                ...existing,
                                paymentMethod:
                                    existing.paymentMethod ||
                                    o.paymentMethod ||
                                    existing.payment?.payment_method ||
                                    null,
                                orderLineItems:
                                    (Array.isArray(existing.orderLineItems) &&
                                        existing.orderLineItems.length > 0
                                        ? existing.orderLineItems
                                        : o.orderLineItems) ||
                                    o.OrderLineItems,
                                tableNumber:
                                    existing.tableNumber ||
                                    o.tableNumber ||
                                    o.table_number ||
                                    null,
                            });
                        } else {
                            byId.set(key, o);
                        }
                    });

                    const sortedOrders = Array.from(byId.values()).sort(
                        (a, b) =>
                            new Date(b.createdDate || b.createdAt) -
                            new Date(a.createdDate || a.createdAt)
                    );
                    setOrders(sortedOrders);
                } else {
                    // For online orders - try to fetch from API as well
                    let apiOrders = [];
                    try {
                        if (selectedRestaurant) {
                            const historyOrders = await fetchRestaurantOrderHistory(selectedRestaurant);
                            apiOrders = historyOrders.filter(order => {
                                const tableNum = order.tableNumber || order.table_number || order.TableNumber;
                                const hasTableNumber = tableNum !== null && tableNum !== undefined && tableNum !== '' && String(tableNum).trim() !== '';
                                return !hasTableNumber;
                            });
                        }
                    } catch (apiError) {
                        console.log('API fetch failed, using localStorage only');
                    }

                    // Combine local and API orders, removing duplicates (online only)
                    const allOrders = [...filteredLocalOrders, ...apiOrders];
                    const uniqueOrders = allOrders.filter((order, index, self) => {
                        const orderId = order.orderId ?? order.OrderId;
                        return index === self.findIndex((o) => (o.orderId ?? o.OrderId) === orderId);
                    });

                    // Sort by created date
                    const sortedOrders = uniqueOrders.sort((a, b) => 
                        new Date(b.createdDate || b.createdAt) - new Date(a.createdDate || a.createdAt)
                    );
                    
                    setOrders(sortedOrders);
                }
            } catch (error) {
                console.error('Error fetching orders:', error);
            }
        };

        fetchOrdersByRestaurant();
    }, [selectedRestaurant, orderTypeFilter]);

    useEffect(() => {
        setHistoryPage(1);
        setHistorySort(null);
    }, [selectedRestaurant, orderTypeFilter]);

    const historyTableKind =
        orderTypeFilter === 'offline' ? 'dineIn' : orderTypeFilter === 'jreats' ? 'jreats' : 'online';

    const getHistorySortValue = (order, sortKey) => {
        if (sortKey === 'childId') {
            return String(order.childId ?? order.child_id ?? '');
        }
        if (sortKey === 'rollNo') {
            return String(order.rollNumber ?? order.roll_number ?? '');
        }
        if (sortKey === 'classSection') {
            const cls = order.childClass ?? order.class;
            const section = order.section;
            const hasClass = cls != null && String(cls).trim() !== '';
            const hasSection = section != null && String(section).trim() !== '';
            if (hasClass && hasSection) return `${cls} - ${section}`;
            if (hasClass) return String(cls);
            if (hasSection) return String(section);
            return '';
        }
        if (sortKey === 'totalPrice') {
            return getOrderSortValue(order, 'totalPrice', historyTableKind);
        }
        return getOrderSortValue(order, sortKey, historyTableKind);
    };

    // Filter orders by order type (offline/online)
    const filteredOrders = orders.filter(order => {
        // Determine if order is offline (has tableNumber) or online (no tableNumber)
        const hasTableNumber = (order.tableNumber !== null && order.tableNumber !== undefined && order.tableNumber !== '') ||
                              (order.table_number !== null && order.table_number !== undefined && order.table_number !== '') ||
                              (order.TableNumber !== null && order.TableNumber !== undefined && order.TableNumber !== '');
        const isOffline = hasTableNumber;
        const isOnline = !hasTableNumber;
        
        // Apply order type filter
        if (orderTypeFilter === 'offline') {
            const type = String(order.orderType || order.order_type || '').toUpperCase();
            const source = String(order.orderSource || order.order_source || '').toUpperCase();
            return (
                isOffline ||
                type === 'DINE-IN' ||
                type === 'TAKEAWAY' ||
                source === 'MB'
            );
        } else {
            return isOnline;
        }
    });

    const sortedHistoryOrders = useMemo(() => {
        if (!historySort?.key) {
            return sortOrderList(filteredOrders, null, historyTableKind);
        }
        return [...filteredOrders].sort((a, b) => {
            const va = getHistorySortValue(a, historySort.key);
            const vb = getHistorySortValue(b, historySort.key);
            let cmp = 0;
            if (typeof va === 'number' && typeof vb === 'number') {
                cmp = va - vb;
            } else {
                cmp = String(va).localeCompare(String(vb), undefined, {
                    numeric: true,
                    sensitivity: 'base',
                });
            }
            return historySort.direction === 'asc' ? cmp : -cmp;
        });
    }, [filteredOrders, historySort, historyTableKind]);

    const { handleSortAsc: handleHistorySortAsc, handleSortDesc: handleHistorySortDesc } =
        buildSortHandlers(setHistorySort, () => setHistoryPage(1));

    const historyTotalPages = Math.max(1, Math.ceil(sortedHistoryOrders.length / historyPageSize));
    const historyPageSafe = Math.min(Math.max(1, historyPage), historyTotalPages);
    const historyPageStart = (historyPageSafe - 1) * historyPageSize;
    const paginatedHistoryOrders = sortedHistoryOrders.slice(
        historyPageStart,
        historyPageStart + historyPageSize
    );

    const handleHistoryPageChange = (page) => {
        setHistoryPage(page);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleHistoryPageSizeChange = (nextSize) => {
        setHistoryPageSize(nextSize);
        setHistoryPage(1);
    };

    const orderDetailPath = (order) => {
        const orderId = order.orderId ?? order.OrderId ?? order.orderNumber ?? order.OrderNumber;
        if (orderId == null || orderId === '') return undefined;
        const restaurantId =
            order.restaurantId ??
            order.RestaurantId ??
            selectedRestaurant ??
            '';
        if (!restaurantId) return undefined;
        return `/OrderDetails/${restaurantId}/${orderId}`;
    };

    const handleOrderIdNavigate = (order) => {
        const path = orderDetailPath(order);
        if (!path) {
            console.warn('Order History: missing restaurant for order details navigation');
            return;
        }
        const fromOrderView =
            orderTypeFilter === 'myeats' || orderTypeFilter === 'jreats' || orderTypeFilter === 'online'
                ? 'online'
                : 'offline';
        navigate(path, {
            state: { fromOrderView, fromOrderHistory: true },
        });
    };

    const renderNameCell = (order, name) => {
        const open = () => handleOrderIdNavigate(order);
        return (
            <td className="orders-name-cell" onClickCapture={nameLinkClickCapture(open)}>
                <AdminNameCell
                    name={name}
                    id={formatDisplayOrderNo(order)}
                    status={orderPillStatus(order.orderStatus)}
                    to={orderDetailPath(order)}
                />
            </td>
        );
    };

    const renderActionsCell = (order, name, onEdit) => (
        <td className="action-cell menu-table-actions-cell">
            <div className="orders-row-actions">
                <AdminRowActions
                    name={name}
                    phone={resolveOrderMobile(order) ?? order.receiverPhone}
                    email={order.customerEmail ?? order.email}
                    onView={() => handleOrderIdNavigate(order)}
                    onEdit={onEdit}
                />
            </div>
        </td>
    );

    return (
        <div className="menu orders-page">
            {(orderTypeFilter === 'myeats' || orderTypeFilter === 'jreats' || orderTypeFilter === 'online') && (
                <>
                    <h3 className="order-history-heading">Select a restaurant</h3>
            <SearchableSelect
                className="order-history-select"
                value={selectedRestaurant}
                onChange={setSelectedRestaurant}
                options={restaurantSelectOptions}
                placeholder="— Select a restaurant —"
                searchPlaceholder="Search restaurants"
                emptyMessage="No restaurants found"
                triggerClassName="add-menu-form-field order-history-select-trigger"
            />
                </>
            )}

            {/* Offline Orders Table */}
            {orderTypeFilter === 'offline' && (
                <div className="order-history-table-container menu-table-wrapper">
                <table className="menu-table order-history-table offline-orders-table orders-name-table">
                    <thead>
                        <tr>
                            <OrdersSortableTh label="Name" sortKey="orderId" activeSortKey={historySort?.key} sortDirection={historySort?.direction} onSortAsc={handleHistorySortAsc} onSortDesc={handleHistorySortDesc} />
                            <OrdersSortableTh label="Mobile#" sortKey="mobile" className="table-col-mobile" activeSortKey={historySort?.key} sortDirection={historySort?.direction} onSortAsc={handleHistorySortAsc} onSortDesc={handleHistorySortDesc} />
                            <OrdersSortableTh label="Food Items" sortKey="foodItems" activeSortKey={historySort?.key} sortDirection={historySort?.direction} onSortAsc={handleHistorySortAsc} onSortDesc={handleHistorySortDesc} />
                            <OrdersSortableTh label="Total Price" sortKey="totalPrice" activeSortKey={historySort?.key} sortDirection={historySort?.direction} onSortAsc={handleHistorySortAsc} onSortDesc={handleHistorySortDesc} />
                            <OrdersSortableTh label="Order Status" sortKey="orderStatus" activeSortKey={historySort?.key} sortDirection={historySort?.direction} onSortAsc={handleHistorySortAsc} onSortDesc={handleHistorySortDesc} />
                            <OrdersSortableTh label="Payment Method" sortKey="paymentMethod" activeSortKey={historySort?.key} sortDirection={historySort?.direction} onSortAsc={handleHistorySortAsc} onSortDesc={handleHistorySortDesc} />
                            <OrdersSortableTh label="Table#" sortKey="tableNumber" activeSortKey={historySort?.key} sortDirection={historySort?.direction} onSortAsc={handleHistorySortAsc} onSortDesc={handleHistorySortDesc} />
                            <OrdersSortableTh label="Created On" sortKey="createdDate" activeSortKey={historySort?.key} sortDirection={historySort?.direction} onSortAsc={handleHistorySortAsc} onSortDesc={handleHistorySortDesc} />
                            <th className="menu-table-actions-head">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {paginatedHistoryOrders.length > 0 ? (
                            paginatedHistoryOrders.map((order) => {
                                const foodItems = order.orderLineItems || order.OrderLineItems || [];
                                const customerName = order.customerName || 'Walk-in';
                                
                                return (
                                    <tr
                                        key={order.orderId}
                                        className="eats-admin-row--clickable"
                                        onClick={rowClickHandler(() => handleOrderIdNavigate(order))}
                                    >
                                        {renderNameCell(order, customerName)}
                                        <td className="table-col-mobile">{formatOrderMobileDisplay(order)}</td>
                                        <td>
                                            {foodItems.length > 0 ? (
                                                <span 
                                                    className="view-food-items-link"
                                                    onClick={() => {
                                                        console.log('Food items:', foodItems);
                                                    }}
                                                >
                                                    View Food Items
                                                </span>
                                            ) : (
                                                'No food items'
                                            )}
                                        </td>
                                        <td>
                                            {orderTypeFilter === 'online'
                                                ? `₹${parseFloat(order.totalPrice || 0).toFixed(2)}`
                                                : parseFloat(order.totalPrice || 0).toFixed(2)}
                                        </td>
                                        <td className="orders-status-cell"><OrderStatusPill status={order.orderStatus || 'NEW'} /></td>
                                        <td>{formatPaymentMethodDisplay(order)}</td>
                                        <td>{formatTableNumberDisplay(order.tableNumber || order.table_number || order.TableNumber)}</td>
                                        <td>{formatOrderDateTime(order.createdDate || order.createdAt)}</td>
                                        {renderActionsCell(order, customerName, () => {
                                            console.log('Edit order:', order.orderId);
                                        })}
                                    </tr>
                                );
                            })
                        ) : (
                            <tr>
                                <td colSpan="9" className="no-orders-message menu-table-empty-cell">No orders data</td>
                            </tr>
                        )}
                    </tbody>
                </table>
                <OrdersTablePagination
                    currentPage={historyPageSafe}
                    totalPages={historyTotalPages}
                    totalItems={sortedHistoryOrders.length}
                    pageSize={historyPageSize}
                    onPageChange={handleHistoryPageChange}
                    onPageSizeChange={handleHistoryPageSizeChange}
                    ariaLabel="Order history pagination"
                />
                </div>
            )}

            {/* Online Orders Table */}
            {(orderTypeFilter === 'myeats' || orderTypeFilter === 'jreats' || orderTypeFilter === 'online') && (
                <div className="order-history-table-container menu-table-wrapper">
                <table className="menu-table order-history-table online-orders-table orders-name-table">
                    <thead>
                        <tr>
                            <OrdersSortableTh label="Name" sortKey="orderId" activeSortKey={historySort?.key} sortDirection={historySort?.direction} onSortAsc={handleHistorySortAsc} onSortDesc={handleHistorySortDesc} />
                            {orderTypeFilter === 'jreats' && (
                                <>
                                    <OrdersSortableTh label="Child ID" sortKey="childId" activeSortKey={historySort?.key} sortDirection={historySort?.direction} onSortAsc={handleHistorySortAsc} onSortDesc={handleHistorySortDesc} />
                                    <OrdersSortableTh label="Roll No" sortKey="rollNo" activeSortKey={historySort?.key} sortDirection={historySort?.direction} onSortAsc={handleHistorySortAsc} onSortDesc={handleHistorySortDesc} />
                                    <OrdersSortableTh label="Class & Section" sortKey="classSection" activeSortKey={historySort?.key} sortDirection={historySort?.direction} onSortAsc={handleHistorySortAsc} onSortDesc={handleHistorySortDesc} />
                                </>
                            )}
                            <OrdersSortableTh label="Food Items" sortKey="foodItems" activeSortKey={historySort?.key} sortDirection={historySort?.direction} onSortAsc={handleHistorySortAsc} onSortDesc={handleHistorySortDesc} />
                            <OrdersSortableTh label={orderTypeFilter === 'online' ? 'Total Price' : 'Total ₹'} sortKey="totalPrice" activeSortKey={historySort?.key} sortDirection={historySort?.direction} onSortAsc={handleHistorySortAsc} onSortDesc={handleHistorySortDesc} />
                            {orderTypeFilter === 'jreats' && (
                                <OrdersSortableTh label="Billing" sortKey="billingType" activeSortKey={historySort?.key} sortDirection={historySort?.direction} onSortAsc={handleHistorySortAsc} onSortDesc={handleHistorySortDesc} />
                            )}
                            <OrdersSortableTh label="Order Status" sortKey="orderStatus" activeSortKey={historySort?.key} sortDirection={historySort?.direction} onSortAsc={handleHistorySortAsc} onSortDesc={handleHistorySortDesc} />
                            <OrdersSortableTh label="Created On" sortKey="createdDate" activeSortKey={historySort?.key} sortDirection={historySort?.direction} onSortAsc={handleHistorySortAsc} onSortDesc={handleHistorySortDesc} />
                            <OrdersSortableTh label="Organization Name" sortKey="organizationName" activeSortKey={historySort?.key} sortDirection={historySort?.direction} onSortAsc={handleHistorySortAsc} onSortDesc={handleHistorySortDesc} />
                            <OrdersSortableTh label="Receiver Name" sortKey="receiverName" activeSortKey={historySort?.key} sortDirection={historySort?.direction} onSortAsc={handleHistorySortAsc} onSortDesc={handleHistorySortDesc} />
                            <th className="menu-table-actions-head">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {paginatedHistoryOrders.length > 0 ? (
                            paginatedHistoryOrders.map((order) => {
                                // Get food items
                                const foodItems = order.orderLineItems || order.OrderLineItems || [];
                                const customerName =
                                    order.customerName ||
                                    (orderTypeFilter === 'jreats' ? order.receiverName : '') ||
                                    'Customer';
                                
                                return (
                            <tr
                                key={order.orderId ?? order.OrderId}
                                className="eats-admin-row--clickable"
                                onClick={rowClickHandler(() => handleOrderIdNavigate(order))}
                            >
                                        {renderNameCell(order, customerName)}
                                        {orderTypeFilter === 'jreats' && (
                                            <>
                                                <td>{order.childId ?? order.child_id ?? 'N/A'}</td>
                                                <td>{order.rollNumber ?? order.roll_number ?? 'N/A'}</td>
                                                <td>
                                                    {(() => {
                                                        const cls = order.childClass ?? order.class;
                                                        const section = order.section;
                                                        const hasClass = cls != null && String(cls).trim() !== '';
                                                        const hasSection = section != null && String(section).trim() !== '';
                                                        if (hasClass && hasSection) return `${cls} - ${section}`;
                                                        if (hasClass) return String(cls);
                                                        if (hasSection) return String(section);
                                                        return 'N/A';
                                                    })()}
                                                </td>
                                            </>
                                        )}
                                <td>
                                            {foodItems.length > 0 ? (
                                                <span 
                                                    className="view-food-items-link"
                                                    onClick={() => {
                                                        console.log('Food items:', foodItems);
                                                    }}
                                                >
                                                    View Food Items
                                                </span>
                                            ) : (
                                                'No food items'
                                            )}
                                </td>
                                        <td>
                                            {orderTypeFilter === 'online'
                                                ? `₹${parseFloat(order.totalPrice || 0).toFixed(2)}`
                                                : parseFloat(order.totalPrice || 0).toFixed(2)}
                                        </td>
                                        {orderTypeFilter === 'jreats' && (
                                            <td>
                                                <OrderBillingCell order={order} />
                                            </td>
                                        )}
                                        <td className="orders-status-cell"><OrderStatusPill status={order.orderStatus || 'NEW'} /></td>
                                        <td>{formatOrderDateTime(order.createdDate || order.createdAt)}</td>
                                        <td>{order.organizationName || 'N/A'}</td>
                                        <td>{order.receiverName || 'N/A'}</td>
                                        {renderActionsCell(order, customerName)}
                                    </tr>
                                );
                            })
                        ) : (
                            <tr>
                                <td colSpan={orderTypeFilter === 'jreats' ? 12 : 8} className="no-orders-message menu-table-empty-cell">No orders data</td>
                            </tr>
                        )}
                    </tbody>
                </table>
                <OrdersTablePagination
                    currentPage={historyPageSafe}
                    totalPages={historyTotalPages}
                    totalItems={sortedHistoryOrders.length}
                    pageSize={historyPageSize}
                    onPageChange={handleHistoryPageChange}
                    onPageSizeChange={handleHistoryPageSizeChange}
                    ariaLabel="Order history pagination"
                />
                </div>
            )}
        </div>
    );
};

export default OrderHistory;
